"use client"

import { BffApiClientMolecule } from "@/app/api-client/store"
import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { BlockchainFiltersView } from "@/app/blockchains/filters/blockchain-filters"
import { getCollectionQueryParams } from "@/app/collections/store/collection-query"

import { useConnectStatus } from "@/app/nft/order/widget/actions/use-connect-status"
import { useWalletConnect } from "@/app/wallet-connect/hooks"
import { Blockchain } from "@anaconda/core/blockchain/types"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  Input,
  cn,
  sonnerToast,
} from "@rarible_int/anaconda_ui_components"
import { useQuery } from "@tanstack/react-query"
import { useMolecule } from "bunshi/react"

import { useCallback, useEffect, useState } from "react"
import { CollectionListingDialog } from "./collection-listing-dialog"

// EVM address validation function
const isValidEVMAddress = (address: string): boolean => {
  // Check if address starts with 0x and is 42 characters long (0x + 40 hex chars)
  const evmAddressRegex = /^0x[a-fA-F0-9]{40}$/
  return evmAddressRegex.test(address)
}

export const COLLECTION_LISTING_PATH = "/collection-listing"

export function CollectionListingForm() {
  const bffApiClient = useMolecule(BffApiClientMolecule)
  const {
    mainnetBlockchains: mainnetBlockchainsAtom,
    isEvmBlockchain: getIsEvmBlockchain,
  } = useMolecule(BlockchainsMoleculeReatom)
  const isEvmBlockchain = getIsEvmBlockchain()
  const mainnetBlockchains = mainnetBlockchainsAtom().filter(isEvmBlockchain)
  const { status } = useWalletConnect()

  const [contractAddress, setContractAddress] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [shouldValidate, setShouldValidate] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedBlockchain, setSelectedBlockchain] = useState<Blockchain>(
    Blockchain.BASE,
  )

  const {
    isLoading,
    error: queryError,
    data: collection,
  } = useQuery({
    ...getCollectionQueryParams(bffApiClient, {
      id: `${selectedBlockchain}:${contractAddress}`,
    }),
    enabled:
      shouldValidate && !!contractAddress && isValidEVMAddress(contractAddress),
    retry: false,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    setError(null)

    if (!contractAddress.trim()) {
      setError("Collection address is required")
      return
    }

    if (!isValidEVMAddress(contractAddress.trim())) {
      setError("Collection address is invalid")
      return
    }

    setShouldValidate(true)
  }

  const connectFunc = useConnectStatus(() => {})

  useEffect(() => {
    if (!shouldValidate) return

    if (queryError && !isLoading) {
      setShouldValidate(false)
      setError("No collection found")
      return
    }

    if (collection?.showcaseListed) {
      setShouldValidate(false)
      setError("Collection is already whitelisted")
      return
    }

    if (collection && collection?.type !== "ERC721") {
      setShouldValidate(false)
      setError("Only ERC721 collections can be listed")
      return
    }

    if (!queryError && !isLoading && collection && contractAddress) {
      setShouldValidate(false)
      setIsDialogOpen(true)
      if (status !== "connected") {
        connectFunc()
      }
    }
  }, [
    shouldValidate,
    queryError,
    isLoading,
    contractAddress,
    collection,
    status,
    connectFunc,
  ])

  return (
    <>
      <form onSubmit={handleSubmit} className="w-full max-w-4xl">
        <div className="flex flex-col gap-[10px]">
          <div className="flex flex-col items-start gap-[10px] md:flex-row">
            <div className="w-full shrink-0 md:w-auto">
              <BlockchainFiltersView
                variant="selector"
                blockchains={mainnetBlockchains}
                selectedBlockchain={selectedBlockchain}
                onSelect={(value) =>
                  setSelectedBlockchain(value as unknown as Blockchain)
                }
                className="h-12 w-full"
              />
            </div>

            <div className="w-full flex-1">
              <Input
                type="text"
                placeholder="Collection contract address..."
                value={contractAddress}
                onChange={(e) => {
                  setContractAddress(e.target.value)
                  if (error) setError(null)
                }}
                className={cn(
                  "h-12 border bg-card bg-transparent text-base text-card-foreground text-white placeholder:text-gray-500 focus-within:border-brand focus-visible:ring-0",
                  error &&
                    "border-red-500 focus:border-red-500 focus:ring-red-500",
                )}
              />
            </div>
          </div>

          <Button
            type="submit"
            loading={isLoading}
            disabled={isLoading}
            className="h-12 w-full whitespace-nowrap bg-brand font-semibold text-base text-black transition-all duration-200 hover:scale-105 disabled:opacity-40"
          >
            <Trans>Approve and list</Trans>
          </Button>

          <p className="mt-2 h-[1.25rem] text-center text-red-500 text-sm">
            {error && <Trans>{error}</Trans>}
          </p>
        </div>
      </form>

      {collection && (
        <CollectionListingDialog
          collectionId={collection.id}
          collectionName={collection.name}
          blockchain={collection.blockchain}
          image={collection.image}
          isOpen={isDialogOpen && status === "connected"}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </>
  )
}
